import React, { useState, useContext } from "react";
import styled from "styled-components";
import {
  DxcButton,
  DxcDialog,
  DxcTextInput,
  DxcSpinner,
  DxcPasswordInput,
  DxcFlex,
  DxcInset,
} from "@dxc-technology/halstack-react";
import {
  login,
  register,
  confirmSignUp,
  getUsername,
} from "../services/login.js";
import { useScreenType } from "./utils.js";
import SessionContext from "../SessionContext.js";

const Login = ({ handleLogin, handleRegister }) => {
  // * useStates
  const [viewType, setViewType] = useState("");
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // * Custom Hooks
  const screenType = useScreenType();
  const session = useContext(SessionContext);

  // * Events
  const onChangeEmail = ({ value }) => setEmail(value);
  const onChangePassword = ({ value }) => setPassword(value);
  const closeDialog = () => {
    navigateReset();
    return handleLogin ? handleLogin() : handleRegister();
  }

  // * Error handler
  const errorHandler = (error, message = undefined) => {
    switch (error.code) {
      case "NotAuthorizedException":
        setErrorMessage("Incorrect username or password.");
        break;
      case "UsernameExistsException":
        setErrorMessage(
          "Username already exists. Please try again with a different one."
        );
        break;
      case "InvalidPasswordException":
        setErrorMessage(
          "Password does not match the requirements. Please try again."
        );
        break;
      case "InvalidParameterException":
        setErrorMessage("Username or password incorrect. Please try again.");
        break;
      case "NotAuthorizedException":
        setErrorMessage("Incorrect username or password.");
        break;
      default:
        setErrorMessage(message || error.message);
        break;
    }
  };

  // * Auth Requests
  const signIn = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      // * sign-in
      const response = await login(username, password);
      // console.log('response :>> ', response);
      updateSession(response);
      handleLogin();
      setIsLoading(false);
    } catch (error) {
      errorHandler(error);
      setIsLoading(false);
    }
  };

  const signUp = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      // * self sign-up
      await register(username, email, password);
      setIsLoading(false);
      navigateConfirmSignIn();
    } catch (error) {
      // "Well, we need to verify that email. Please check your inbox!";
      errorHandler(error);
      setIsLoading(false);
    }
  };

  const handlerConfirmSignUp = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      // * Verification code 
      await confirmSignUp(username, code);
      // * update Session and sign-in
      const response = await login(username, password);
      // console.log('response :>> ', response);
      updateSession(response);
      // ? Flag
      setIsLoading(false);
      navigateReset();
      handleRegister();
    } catch (error) {
      // "Well, we need to verify that email. Please check your inbox!";
      errorHandler(error);
      setIsLoading(false);
    }
  };

  const updateSession = (response) => {
    session.setApiKey(response.xApiKey);
    session.setAuthorization(response.Authorization);
    getUsername() && session.setUsername(getUsername().username);
  };

  const navigateReset = () => {
    setViewType("");
  };

  const navigateConfirmSignIn = () => {
    setIsLoading(false);
    // setIsErrorVisible(false);
    setViewType("confirmSignIn");
  };

  return (
    <React.Fragment>
      <DxcDialog
        overlay={true}
        isCloseVisible={true}
        onCloseClick={closeDialog}
      >
        <DxcInset space="2rem">
          {viewType === "confirmSignIn" ? (
            <>
              <DxcFlex direction="column" gap="3.75rem" alignSelf="center">
                <DxcFlex justifyContent="center" alignItems="center">
                  <LoginTitle screenType={screenType}>
                    Confirm Verification Code
                  </LoginTitle>
                  {isLoading && <DxcSpinner mode="small" />}
                </DxcFlex>
                <DxcFlex
                  direction="column"
                  gap="3.75rem"
                  alignItems="flex-start"
                >
                  <DxcFlex
                    direction="column"
                    gap="1.5rem"
                    alignSelf="center"
                    alignItems="flex-start"
                  >
                    <DxcTextInput
                      label="Verification code"
                      value={code}
                      onChange={({ value }) => setCode(value)}
                      size={
                        screenType === "desktop"
                          ? "large"
                          : screenType === "tablet"
                          ? "medium"
                          : "fillParent"
                      }
                      clearable
                    />
                    {errorMessage && (
                      <ErrorMessage>{errorMessage}</ErrorMessage>
                    )}
                  </DxcFlex>
                  <DxcFlex alignSelf="center">
                    <DxcButton
                      label="Submit"
                      disabled={isLoading}
                      onClick={handlerConfirmSignUp}
                      size={screenType !== "mobile" ? "large" : "fillParent"}
                    />
                  </DxcFlex>
                </DxcFlex>
              </DxcFlex>
            </>
          ) : (
            <DxcFlex direction="column" gap="3.75rem" alignSelf="center">
              <DxcFlex justifyContent="center" alignItems="center">
                {(handleLogin && (
                  <LoginTitle screenType={screenType}>Sign in</LoginTitle>
                )) || <LoginTitle screenType={screenType}>Register</LoginTitle>}
                {isLoading && <DxcSpinner mode="small" />}
              </DxcFlex>
              <DxcFlex direction="column" gap="3.75rem" alignItems="flex-start">
                <DxcFlex
                  direction="column"
                  gap="1.5rem"
                  alignSelf="center"
                  alignItems="flex-start"
                >
                  <DxcTextInput
                    label="Username"
                    value={username}
                    onChange={({ value }) => setUsername(value)}
                    size={
                      screenType === "desktop"
                        ? "large"
                        : screenType === "tablet"
                        ? "medium"
                        : "fillParent"
                    }
                    clearable
                  />
                  {handleRegister && (
                    <DxcTextInput
                      label="Email"
                      value={email}
                      onChange={onChangeEmail}
                      size={
                        screenType === "desktop"
                          ? "large"
                          : screenType === "tablet"
                          ? "medium"
                          : "fillParent"
                      }
                      clearable
                    />
                  )}
                  <DxcPasswordInput
                    label="Password"
                    value={password}
                    onChange={onChangePassword}
                    size={
                      screenType === "desktop"
                        ? "large"
                        : screenType === "tablet"
                        ? "medium"
                        : "fillParent"
                    }
                    clearable
                  />
                  {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
                </DxcFlex>
                <DxcFlex alignSelf="center">
                  {handleLogin && (
                    <DxcButton
                      label="SIGN IN"
                      disabled={isLoading}
                      onClick={signIn}
                      size={screenType !== "mobile" ? "large" : "fillParent"}
                    />
                  )}
                  {handleRegister && (
                    <DxcButton
                      label="SIGN UP"
                      disabled={isLoading}
                      onClick={signUp}
                      size={screenType !== "mobile" ? "large" : "fillParent"}
                    />
                  )}
                </DxcFlex>
              </DxcFlex>
            </DxcFlex>
          )}
        </DxcInset>
      </DxcDialog>
    </React.Fragment>
  );
};

const LoginTitle = styled.div`
  width: 100%;
  max-width: ${(props) => (props.screenType === "desktop" ? "480px" : "360px")};
  font-size: 34px;
`;

const ErrorMessage = styled.div`
  align-self: center;
  font-size: 14px;
  background-color: #fff6f6;
  color: #9f3a38;
  box-shadow: inset 0 0 0 1px #e0b4b4, 0 0 0 0 transparent;
  padding: 14px 12px;
  border-radius: 5px;
`;

export default Login;
